import React, { useContext, useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from "react";

import { BoardContext, CardContext, SlotMachineContext, SocketContext, TeamContext, UserContext } from "../../../context";

import { FrontSide, MainButton, PinkNotification } from "../../atoms";
import { ICard } from "../../../interfaces";
import { useWindowDimensions, useSound } from "../../../hooks";
import { CardType } from "../../../util/enums";
import { useTranslation } from "react-i18next";

export interface SlotMachineRefObject {
    dropedStrenghtCard(): void;
}

const SlotMachineChosenCardHolder = forwardRef((_props: {}, ref: React.Ref<SlotMachineRefObject>) => {
    useImperativeHandle(ref, () => ({ dropedStrenghtCard }));

    const { t } = useTranslation();
    const { socket } = useContext(SocketContext);
    const { game } = useContext(TeamContext);
    const { 
        draggableCard, 
        isStrengthCardCurrentlyDragged, 
        setIsStrengthCardCurrentlyDragged 
    } = useContext(CardContext);
    const { userData } = useContext(UserContext);
    const {
        setShowIt, 
        confirmSelectionDisabled, 
        setConfirmSelectionDisabled, 
        setIsConfirmSelectionClicked, 
        setLastDroppedCard, 
        allStrengthCardsIdsState, 
        isConfirmSelectionClicked, 
        verticalCardIndexOfSelectedRow, 
        verticalCardIdsAndTheirIndexes,
        animateWhiteShaddowGlowingCards,
        setAnimateWhiteShaddowGlowingCards,
        setWaitForTheOthersModal,
        isConfirmButtonTipShown,
        setIsConfirmButtonTipShown
    } = useContext(SlotMachineContext);
    const { didUserRejoinTheGame, setDidUserRejoinTheGame } = useContext(BoardContext);

    const [chosenStrengthCard, setChosenStrengthCard] = useState<(ICard | undefined)[]>(new Array(5));
    const { width } = useWindowDimensions();
    const alertRef = useRef<NodeJS.Timeout>();
    const [selectedIndex, setSelectedIndex] = useState<(number | null)>(null);
    const [isArrowShown, setIsArrowShown] = useState<boolean>(true);
    const {playSound} = useSound();
    const demoCard: ICard = {
        type: CardType.STRENGTH,
        description: "0",
        id: 1111,
    }

    useEffect(() => {
        if (!userData) return;

        let currentStrengthCardsFromGameObject = game?.gameInfo.players[userData?.email].currentStrengthCards;
        const numberOfSelectedStrengthCardsFromSlotMachine = currentStrengthCardsFromGameObject.length;

        if (numberOfSelectedStrengthCardsFromSlotMachine)
           setAnimateWhiteShaddowGlowingCards(false);

        let mappedArryOfStrengthCardsObjectsFittedForFrontend = currentStrengthCardsFromGameObject.map((strengthCardFromBackEnd: any) => {
            return convertRecievedObjectToStrengthCardObject(strengthCardFromBackEnd);
        })

        if (numberOfSelectedStrengthCardsFromSlotMachine && didUserRejoinTheGame) {
            setChosenStrengthCard(mappedArryOfStrengthCardsObjectsFittedForFrontend);
            setDidUserRejoinTheGame(false);
        }

    }, [didUserRejoinTheGame, game?.gameInfo.players, setAnimateWhiteShaddowGlowingCards, setDidUserRejoinTheGame, userData]);

    const numberOfPopulatedCards = useCallback(() => {
        //TAG _id
        return chosenStrengthCard.filter(card => card?.id !== undefined).length;
    }, [chosenStrengthCard]);

    useEffect(() => {
        if (numberOfPopulatedCards() === 5 && !isConfirmSelectionClicked)
            return setConfirmSelectionDisabled(false);

        return setConfirmSelectionDisabled(true);
    }, [chosenStrengthCard, setConfirmSelectionDisabled, numberOfPopulatedCards, isConfirmSelectionClicked]);

    const convertRecievedObjectToStrengthCardObject = (strengthCardFromBackEnd: any) => {
        //TAG _id
        return {
            backImagePath: "",
            description: strengthCardFromBackEnd.description,
            id: strengthCardFromBackEnd._id | 0,
            type: CardType.STRENGTH
        }
    }

    const isCardChoosen = (draggableCard: ICard) => {
        return chosenStrengthCard.filter(card => {
            if (card) return card.id === draggableCard.id
            return false;
        }).length;
    }

    const dropedStrenghtCard = () => {
        if (selectedIndex === null) return;
        if (draggableCard && isStrengthCardCurrentlyDragged) {

            if (isCardChoosen(draggableCard)) {
                activateNotificatoinForTwoSeconds();
                return;
            }

            if (numberOfPopulatedCards() < 5) {
                const copyOfChoosen: ICard[] = [];
                const choosenCards = Object.assign(copyOfChoosen, chosenStrengthCard)

                if (choosenCards[selectedIndex]) return;

                choosenCards[selectedIndex] = draggableCard;

                setChosenStrengthCard(choosenCards);
                socket?.emit("chooseStrengthCard", { id: draggableCard.id, idsOfCardsInSlotMachine: allStrengthCardsIdsState.current });

                verticalCardIndexOfSelectedRow.current = verticalCardIdsAndTheirIndexes?.find((card: any) => draggableCard.id === card.cardId)?.verticalIndex;
                setLastDroppedCard(draggableCard);
            }
        }
        setIsStrengthCardCurrentlyDragged(false);
    }

    const activateNotificatoinForTwoSeconds = () => {
        setShowIt(true);

        if (alertRef.current) {
            clearTimeout(alertRef.current);
        }

        alertRef.current = setTimeout(() => {
            setShowIt(false);
        }, 3000)
    }

    const removeChosenCard = (cardId: number | string, index: number) => {
        const copyOfChoosen: (ICard | undefined)[] = [];
        const choosenCards = Object.assign(copyOfChoosen, chosenStrengthCard)

        setSelectedIndex(null);
        choosenCards[index] = undefined;
        setChosenStrengthCard(choosenCards);
        socket?.emit("dropStrengthCard", { id: cardId });
        playSound("/sounds/CloseCard.mp3");

        return setConfirmSelectionDisabled(true);
    }
    
    const draggedStrengthCardsFields = (index: number) => {
        return (
            <div onMouseEnter={() => setSelectedIndex(index)}
                onMouseLeave={() => setSelectedIndex(null)} className="drop_container__content center_horizontally_and_vertically" style={{ minHeight: width * 0.155, minWidth: width * 0.118 }}>
                {(chosenStrengthCard[index] && !isConfirmSelectionClicked) &&
                    <div className="slot_machine_lower_part_clear_x" onClick={() => removeChosenCard(chosenStrengthCard[index]!.id!, index)}> </div>
                }
                {chosenStrengthCard[index] && <FrontSide cardData={chosenStrengthCard[index] || demoCard} />}
            </div>
        )
    }
    
    const SlotMachineChosenCards = () => {
        return Array.apply(null, Array(5)).map((_item: any, index: number) => {
            return (
                <div key={index} style={{ marginRight: "5%" }}>
                    <div style={{ position: "relative" }}> <div className="drop_containter_parent" style={{ minHeight: width * 0.156, minWidth: width * 0.12 }} ></div> </div>
                    <div className={` ${animateWhiteShaddowGlowingCards ? "slot_machine_placeholder_border_initial_animation_with_white_shaddow" : "slot_machine_placeholder_border"} `}>
                        {draggedStrengthCardsFields(index)}
                        {index === 4 && animateWhiteShaddowGlowingCards &&
                        <div style={{position: "absolute", right: 0, zIndex: 19, bottom: "10vw"}}> 
                            <PinkNotification notificationWidthVW={20} notificationRenderingSide="right" text={t("tipDragYourChosenStrengthCardsToYourPersonalBoard")} moveFromTop={20} />
                        </div> 
                        }
                    </div>
                    
                </div>
            )
        })
    }

    const confirmSpin = () => {
        setIsArrowShown(false);
        socket?.emit("finishChoosingCards"); 
        setConfirmSelectionDisabled(true);
        setIsConfirmSelectionClicked(true);
        setWaitForTheOthersModal(true);
    };

    return (
        <div className="slot_machine_lower_part_wrapper" >
            <div className="slot_machine_lower_horizontal_line">
                <div className="linear_gradient_line_horizontal"> </div>
            </div>

            <div style={{ width: "100%", display: "flex" }}>
                <div className="drop_container">
                    {SlotMachineChosenCards()}
                </div>               
            </div>

            <div style={{ marginTop: width * 0.02, width: '20%' }} className={` ${ (isArrowShown && !confirmSelectionDisabled && isConfirmButtonTipShown) ? "glowing_white_shaddow_with_border_radius" : ""} slot_machine__confirm_button`}>
                
                {isArrowShown && !confirmSelectionDisabled && isConfirmButtonTipShown &&
                    <div style={{position: "relative", bottom: "2vw", left: "2vw", zIndex: 19}}>
                        <PinkNotification notificationWidthVW={25} notificationRenderingSide="top" text={t("ifYouFinishedSelectingPleaseClickOnTheConfirmText")} moveFromTop={20} hasCloseButton={true} closeTheNotification={() =>  setIsConfirmButtonTipShown(false)}/>
                    </div>
                }
                
                <MainButton type="big" text={t("confirmText")} clickHandler={confirmSpin} disabled={confirmSelectionDisabled} />
            </div>
        </div>
    );
});

export default SlotMachineChosenCardHolder;